import { SalesChart } from './SalesChart';
import { parseFilters } from '@utils/parseFilters';
import useToastContext from '@contexts/ToastContext/hook';
import React, { useEffect, useState } from 'react';
import { Typography } from '@vibTheme/components/_base/Typography';
import { CustomHelpTooltip } from './CustomHelpTooltip';
import { ProfitIndicator } from './ProfitIndicator';
import { getDefaultMessage } from '@utils/getDefaultDashboardTipMessage';
import { findSalesSummary } from '@api/reports';
import {
  SalesByDate,
  SalesSummary,
  SalesSummaryFilters,
} from '@interfaces/reports/salesSummary';
import parseDate from '@utils/parseDate';
import { resolveVariation } from '@utils/resolveVariation';
import {
  canCompare,
  perSaleForComparison,
  resolvePerSale,
  resolveReportedPerSale,
} from '@utils/resolvePerSale';
import { MetricCard } from './PaymentConversion/shared';
import { formatMoney, formatRate } from './reportFormat';

type Props = {
  filters: SalesSummaryFilters;
};

/**
 * A dash when there is nothing to divide, never a formatted zero.
 *
 * "0,0%" on a period with no sales reads as a perfect refund rate, and "R$ 0,00" as a worthless average ticket.
 * Both are claims about sales that did not happen.
 */
const describePerSale = (
  perSale: ReturnType<typeof resolvePerSale>,
  format: (value: number) => string,
) => (perSale.kind === 'value' ? format(perSale.value) : '—');

export function SalesCharts(props: Props) {
  const { filters } = props;

  const toast = useToastContext();
  const [data, setData] = useState<SalesSummary>(salesSummaryInitialState);
  const [previousPeriodData, setPreviousPeriodData] = useState<SalesSummary>(
    salesSummaryInitialState,
  );
  const [isLoading, setIsLoading] = useState(false);
  /**
   * A request that FAILED, as opposed to a period with no sales.
   *
   * The catch resets the totals to the all-zero initial state, and the derived rates then read as "nenhuma venda
   * no período" - a network or server failure presented as an empty month. The four raw cards above have always
   * had the same shape of problem, but a zero total at least reads as a total; a caption asserting there were no
   * sales is a claim about what happened. Codex caught it on #265.
   */
  const [hasFailed, setHasFailed] = useState(false);
  /**
   * Whether the PREVIOUS period's request has answered, tracked apart from isLoading.
   *
   * Both requests share isLoading and each clears it in its own finally, so the first to finish hides the spinner
   * while the other is still in flight. For a total that only matters cosmetically; for a COMPARISON it is wrong
   * data: previousPeriodData is still the all-zero initial state, resolveVariation reads a previous zero as
   * "novo", and the badge announces a first-ever sale on a community that has sold for years. A rejected prior
   * request makes it permanent rather than momentary. Codex caught it on #265.
   */
  const [hasPriorData, setHasPriorData] = useState(false);

  /**
   * Both rates, for this period and the previous one, derived from the responses already in hand.
   *
   * Computed here rather than inside the cards so the badge compares the same two numbers the cards print.
   */
  const refundRate = resolveReportedPerSale(
    data?.refundRate,
    +(data?.refundTotalCount || 0),
    +(data?.salesTotalCount || 0),
  );
  const priorRefundRate = resolveReportedPerSale(
    previousPeriodData?.refundRate,
    +(previousPeriodData?.refundTotalCount || 0),
    +(previousPeriodData?.salesTotalCount || 0),
  );
  const averageTicket = resolveReportedPerSale(
    data?.averageTicket,
    +(data?.salesTotalValue || 0),
    +(data?.salesTotalCount || 0),
  );
  const priorAverageTicket = resolveReportedPerSale(
    previousPeriodData?.averageTicket,
    +(previousPeriodData?.salesTotalValue || 0),
    +(previousPeriodData?.salesTotalCount || 0),
  );

  /**
   * The pair of requests, with the superseded ones ignored.
   *
   * `active` is the guard every other report hook in this repo already has - useBotFunnel and useRecurringRevenue
   * both close over one - and this component was the last without it. Two separate defects came from that, both
   * found by codex on #265, and they are mirror images:
   *
   *   - the PREVIOUS request answering first cleared the shared isLoading while `data` was still the initial state,
   *     so the cards rendered "nenhuma venda no período" on first load, or compared a stale current rate after a
   *     filter change;
   *   - a superseded PREVIOUS request could still resolve after the effect had reset the flag, marking an old
   *     range's answer as valid, so all six badges compared the new current period against the old previous one -
   *     permanently, if the stale request finished last.
   *
   * Readiness is tracked per request rather than by one shared boolean, because "both settled" is the only state in
   * which a comparison between them means anything.
   */
  useEffect(() => {
    let active = true;

    if (!filters.botConfigIds?.length) {
      setData(salesSummaryInitialState);
      setPreviousPeriodData(salesSummaryInitialState);
      setHasPriorData(false);
      setHasFailed(false);
      setIsLoading(false);
      return () => {
        active = false;
      };
    }

    setIsLoading(true);
    setHasPriorData(false);
    setHasFailed(false);

    const parsed = parseFilters(filters);

    const current = findSalesSummary(parsed)
      .then((response) => {
        if (!active) return;
        setData(response.data);
        setHasFailed(false);
      })
      .catch((error) => {
        if (!active) return;
        toast.handleError(error, 'Não foi possível carregar os registros');
        setHasFailed(true);
        setData(salesSummaryInitialState);
      });

    const prior = findSalesSummary({
      ...parsed,
      startDate: parsed.previousPeriodStartDate,
      endDate: parsed.previousPeriodEndDate,
    })
      .then((response) => {
        if (!active) return;
        setPreviousPeriodData(response.data);
        // Only here, only on success, and only while this effect is still the current one.
        setHasPriorData(true);
      })
      .catch((error) => {
        if (!active) return;
        toast.handleError(error, 'Não foi possível carregar os registros');
        setPreviousPeriodData(salesSummaryInitialState);
      });

    // Cleared once BOTH have settled, not by whichever finishes first: the spinner used to disappear while half
    // the numbers on screen were still the previous selection's.
    Promise.all([current, prior]).finally(() => {
      if (active) setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, [filters]);

  const getSalesDefaultMessage = (
    type: any,
    currentTotal: number,
    previousTotal: number,
  ) => {
    // Guarded for the same reason as ProfitIndicator: an empty previous period used to reach
    // getDefaultMessage as Infinity and get printed into a sentence the producer reads.
    const variation = resolveVariation(currentTotal, previousTotal);
    const percentage = variation.kind === 'change' ? variation.percent : 0;

    return getDefaultMessage(type, currentTotal, previousTotal, percentage);
  };

  return (
    <section className="pb-main">
      <div className="pb-main">
        <Typography variant="h2">Período Atual</Typography>
        <Typography className="text-sm text-lightBlack">
          de {parseDate(filters.startDate, { type: 'date' })} a{' '}
          {parseDate(filters.endDate, { type: 'date' })}
        </Typography>
      </div>

      <div className="grid gap-main md:grid-cols-2 2xl:grid-cols-4">
        <SalesChart
          title="Vendas (quantidade)"
          customHelpTooltip={
            <CustomHelpTooltip
              description={getSalesDefaultMessage(
                'salesCountInfo',
                +(data?.salesTotalCount || 0),
                +(previousPeriodData?.salesTotalCount || 0),
              )}
            />
          }
          profitIndicator={
            hasPriorData ? (
              <ProfitIndicator
                current={+(data?.salesTotalCount || 0)}
                previous={+(previousPeriodData?.salesTotalCount || 0)}
              />
            ) : null
          }
          isLoading={isLoading}
          label={data?.salesTotalCount || ''}
          chartData={data?.sales?.map((sale: SalesByDate) => {
            return {
              date: sale.date,
              value: +sale.salesCount,
            };
          })}
        />
        <SalesChart
          title="Vendas (valor líquido)"
          customHelpTooltip={
            <CustomHelpTooltip
              description={getSalesDefaultMessage(
                'salesValueInfo',
                +(data?.salesTotalValue || 0),
                +(previousPeriodData?.salesTotalValue || 0),
              )}
            />
          }
          profitIndicator={
            hasPriorData ? (
              <ProfitIndicator
                current={+(data?.salesTotalValue || 0)}
                previous={+(previousPeriodData?.salesTotalValue || 0)}
              />
            ) : null
          }
          isLoading={isLoading}
          isMoneyChart
          label={data?.salesTotalValue || ''}
          chartData={data?.sales?.map((sale: SalesByDate) => {
            return {
              date: sale.date,
              value: +sale.paymentValue,
            };
          })}
        />
        <SalesChart
          title="Reembolsos (quantidade)"
          customHelpTooltip={
            <CustomHelpTooltip
              description={getSalesDefaultMessage(
                'refundsCountInfo',
                +(data?.refundTotalCount || 0),
                +(previousPeriodData?.refundTotalCount || 0),
              )}
            />
          }
          profitIndicator={
            hasPriorData ? (
              <ProfitIndicator
                invertLogic
                current={+(data?.refundTotalCount || 0)}
                previous={+(previousPeriodData?.refundTotalCount || 0)}
              />
            ) : null
          }
          isLoading={isLoading}
          label={data?.refundTotalCount || ''}
          chartData={data?.sales?.map((sale: SalesByDate) => {
            return {
              date: sale.date,
              value: +sale.refundCount,
            };
          })}
        />
        <SalesChart
          title="Reembolsos (valor líquido)"
          customHelpTooltip={
            <CustomHelpTooltip
              description={getSalesDefaultMessage(
                'refundsValueInfo',
                +(data?.refundTotalValue || 0),
                +(previousPeriodData?.refundTotalValue || 0),
              )}
            />
          }
          profitIndicator={
            hasPriorData ? (
              <ProfitIndicator
                invertLogic
                current={+(data?.refundTotalValue || 0)}
                previous={+(previousPeriodData?.refundTotalValue || 0)}
              />
            ) : null
          }
          isLoading={isLoading}
          isMoneyChart
          label={data?.refundTotalValue || ''}
          chartData={data?.sales?.map((sale: SalesByDate) => {
            return {
              date: sale.date,
              value: +sale.refundValue,
            };
          })}
        />
      </div>

      {/*
        The two rates the four cards above already contain and nobody computed.
        
        platform-front#245 asked for four rate cards on the root and one shipped; #259 carries the rest. These two
        need no new request: refunds over sales and net value over sales come out of the response already fetched.
        
        Presented as MetricCard rather than SalesChart because neither has a daily series worth drawing - a rate
        per day is 0% or 100% for any community selling a handful a day, which reads as volatility that is not
        there. The card matches "Taxa de pagamento", the one rate card already on this page.
      */}
      <div className="pt-main grid gap-main md:grid-cols-2">
        <MetricCard
          title="Taxa de reembolso"
          isLoading={isLoading}
          value={hasFailed ? '—' : describePerSale(refundRate, formatRate)}
          caption={
            hasFailed
              ? 'não foi possível carregar'
              : refundRate.kind === 'none'
              ? 'nenhuma venda no período'
              : `${+(data?.refundTotalCount || 0)} de ${+(
                  data?.salesTotalCount || 0
                )} vendas`
          }
          indicator={
            // No badge without a current rate to compare: mapping absence to zero made an empty period report a
            // 100% drop beside a card refusing to state any rate at all.
            !hasFailed && hasPriorData && canCompare(refundRate) ? (
              <ProfitIndicator
                // Going up is bad, the same flag the two refund cards above already carry. Without it a rising
                // refund rate would be painted green.
                invertLogic
                current={perSaleForComparison(refundRate)}
                previous={perSaleForComparison(priorRefundRate)}
              />
            ) : null
          }
          help="Das vendas pagas no período, o percentual que foi reembolsado. Conta quantidade, não valor: dez vendas com um reembolso dão 10%, independentemente de quanto cada uma valia. Tudo é contado pela data da VENDA, inclusive os cards de reembolso acima — um reembolso de hoje sobre uma venda do mês passado não entra em nenhum dos dois, ele conta no mês da venda. Por isso a taxa nunca passa de 100%: quem foi reembolsado é sempre um subconjunto de quem comprou no período. Sem venda no período não há taxa, e o card mostra um traço em vez de 0%."
        />
        <MetricCard
          title="Ticket médio"
          isLoading={isLoading}
          value={hasFailed ? '—' : describePerSale(averageTicket, formatMoney)}
          caption={
            hasFailed
              ? 'não foi possível carregar'
              : averageTicket.kind === 'none'
              ? 'nenhuma venda no período'
              : `valor líquido ÷ ${+(data?.salesTotalCount || 0)} vendas`
          }
          indicator={
            !hasFailed && hasPriorData && canCompare(averageTicket) ? (
              <ProfitIndicator
                current={perSaleForComparison(averageTicket)}
                previous={perSaleForComparison(priorAverageTicket)}
              />
            ) : null
          }
          help="Quanto cada venda do período valeu, em média, pelo valor LÍQUIDO — depois da taxa da plataforma. Inclui venda avulsa: pack, chamada de vídeo e oferta personalizada entram, então este número não é comparável com 'Ticket médio mensal' da recorrência, que divide o MRR pelos assinantes ativos e normaliza tudo para 30 dias. São contas diferentes com nomes parecidos: este é por VENDA, o outro é por ASSINANTE por mês."
        />
      </div>

      <div className="py-main">
        <Typography variant="h2">Período Anterior</Typography>
        <Typography className="text-sm text-lightBlack">
          de {parseDate(filters.previousPeriodStartDate, { type: 'date' })} a{' '}
          {parseDate(filters.previousPeriodEndDate, { type: 'date' })}
        </Typography>
      </div>
      <div className="grid gap-main md:grid-cols-2 2xl:grid-cols-4">
        <SalesChart
          title="Vendas (quantidade)"
          isLoading={isLoading}
          label={previousPeriodData?.salesTotalCount || ''}
          chartData={previousPeriodData?.sales?.map((sale: SalesByDate) => {
            return {
              date: sale.date,
              value: +sale.salesCount,
            };
          })}
        />
        <SalesChart
          title="Vendas (valor líquido)"
          isLoading={isLoading}
          isMoneyChart
          label={previousPeriodData?.salesTotalValue || ''}
          chartData={previousPeriodData?.sales?.map((sale: SalesByDate) => {
            return {
              date: sale.date,
              value: +sale.paymentValue,
            };
          })}
        />
        <SalesChart
          title="Reembolsos (quantidade)"
          isLoading={isLoading}
          label={previousPeriodData?.refundTotalCount || ''}
          chartData={previousPeriodData?.sales?.map((sale: SalesByDate) => {
            return {
              date: sale.date,
              value: +sale.refundCount,
            };
          })}
        />
        <SalesChart
          title="Reembolsos (valor líquido)"
          isLoading={isLoading}
          isMoneyChart
          label={previousPeriodData?.refundTotalValue || ''}
          chartData={previousPeriodData?.sales?.map((sale: SalesByDate) => {
            return {
              date: sale.date,
              value: +sale.refundValue,
            };
          })}
        />
      </div>
    </section>
  );
}

const salesSummaryInitialState: SalesSummary = {
  refundTotalCount: '0',
  refundTotalValue: '0',
  sales: [],
  salesTotalCount: '0',
  salesTotalValue: '0',
};
